import { useCallback, useEffect, useRef, useState } from "react";
import { readFileAsDataUrl } from "../lib/image-input";

/**
 * Turns a picked image file into an `onImageSelect(dataUrl, file)` call for a
 * caller that holds a limited number of images.
 *
 * Reading goes through an async downscale that can take a noticeable fraction
 * of a second for a large photo, so picks can overlap. Each pick reserves one
 * of `slots` (how many more images the caller accepts right now) as soon as it
 * starts and releases it when it lands, so picks still in flight count against
 * the limit. Without the reservation every overlapping pick sees the same
 * count, and a quick second drop or paste could exceed the limit.
 *
 * `isPending` is true while any pick is in flight. Callers disable submitting
 * on it, since a submit in that window would go out without the new image.
 */
export function useImageIntake(
  slots: number,
  onImageSelect: (dataUrl: string, file: File) => void,
): { intake: (file: File) => void; isPending: boolean } {
  // The ref gates picks synchronously, before a re-render could update the
  // count; the state is what re-renders callers when it changes.
  const inFlight = useRef(0);
  const [pendingCount, setPendingCount] = useState(0);
  // A pick lands after later renders, so it must call the newest handler
  // rather than the one captured when the pick started.
  const onImageSelectRef = useRef(onImageSelect);
  useEffect(() => {
    onImageSelectRef.current = onImageSelect;
  });

  const intake = useCallback(
    (file: File) => {
      if (inFlight.current >= slots) return;
      inFlight.current++;
      setPendingCount((n) => n + 1);
      readFileAsDataUrl(file, (dataUrl, scaled) => {
        inFlight.current--;
        setPendingCount((n) => n - 1);
        onImageSelectRef.current(dataUrl, scaled);
      });
    },
    [slots],
  );

  return { intake, isPending: pendingCount > 0 };
}
